import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProviderPageLayout from '../../components/ProviderPageLayout';
import { useAuth } from '../../auth/useAuth';
import { getPostLoginPath } from '../../services/billingApi';

const ProviderVerify = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');

  const navigate = useNavigate();
  const { confirmSignUp, resendSignUpCode, signIn } = useAuth();

  useEffect(() => {
    const pendingUsername = sessionStorage.getItem('pendingUsername');
    const pendingEmail = sessionStorage.getItem('pendingEmail');
    if (pendingUsername) setUsername(pendingUsername);
    if (pendingEmail) setEmail(pendingEmail);
    if (!pendingUsername && !pendingEmail) navigate('/provider/signup');
  }, [navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!code || code.length < 6) {
      setError('Please enter the 6-digit code from your email.');
      setLoading(false);
      return;
    }

    try {
      await confirmSignUp(username, code);
      const pendingPassword = sessionStorage.getItem('pendingSignupPassword');

      if (!pendingPassword) {
        setSuccess('Email verified. Please sign in.');
        sessionStorage.removeItem('pendingUsername');
        sessionStorage.removeItem('pendingEmail');
        setTimeout(() => navigate('/provider/login'), 2000);
        return;
      }

      await signIn(username, pendingPassword);
      sessionStorage.removeItem('pendingUsername');
      sessionStorage.removeItem('pendingEmail');
      sessionStorage.removeItem('pendingSignupPassword');
      setSuccess('Verified! Setting up your account…');
      const nextPath = await getPostLoginPath();
      setTimeout(() => navigate(nextPath), 800);
    } catch (err) {
      if (err.code === 'CodeMismatchException') {
        setError('Invalid code. Check your email and try again.');
      } else if (err.code === 'ExpiredCodeException') {
        setError('Code expired. Request a new one below.');
      } else if (err.code === 'NotAuthorizedException') {
        setError('Already verified. Redirecting to sign in…');
        setTimeout(() => navigate('/provider/login'), 2000);
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!username) return;
    setError('');
    setResending(true);
    try {
      await resendSignUpCode(username);
      setSuccess('A new code has been sent to your email.');
    } catch (err) {
      setError(err.message || 'Could not resend code.');
    } finally {
      setResending(false);
    }
  };

  return (
    <ProviderPageLayout
      variant="split"
      splitImage="/images/gallery-table.png"
      splitEyebrow="One last step"
      splitQuote="Confirm it's you, and your provider workspace is ready to go."
    >
      <div className="provider-card provider-card--auth">
        <p className="provider-eyebrow">Almost there</p>
        <h1 className="provider-title">Verify your email</h1>
        <p className="provider-subtitle">
          {email
            ? `We sent a 6-digit code to ${email}`
            : 'Enter the verification code from your email'}
        </p>

        {error && <div className="provider-alert provider-alert--error">{error}</div>}
        {success && <div className="provider-alert provider-alert--success">{success}</div>}

        <form onSubmit={handleVerify}>
          <div className="provider-form-group">
            <label htmlFor="code" className="provider-label">Verification code</label>
            <input
              type="text"
              id="code"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              className="provider-input provider-input--code"
              placeholder="000000"
              maxLength={6}
              autoComplete="one-time-code"
            />
          </div>
          <button type="submit" disabled={loading} className="provider-btn provider-btn--primary">
            {loading ? 'Verifying…' : 'Verify & continue'}
          </button>
        </form>

        <div className="provider-footer-links">
          <button
            type="button"
            className="provider-link"
            onClick={handleResendCode}
            disabled={resending}
          >
            {resending ? 'Sending…' : "Didn't get a code? Resend"}
          </button>
        </div>
      </div>
    </ProviderPageLayout>
  );
};

export default ProviderVerify;
